/**
 * Course-landing theme helpers — JS port of the Blade @php color functions
 * (parseColor / lightenColor / darkenColor) used by the legacy templates.
 *
 * The admin saves three colours per course (primary, button-text, footer-bg).
 * Each template derives an accent system (hover/light/soft/glow/rgb) from the
 * primary, exactly like the server-rendered Blade did. We expose them as CSS
 * custom properties so the JSX templates can consume them via Tailwind
 * arbitrary values, e.g. `bg-[var(--cl-primary)]`, and the colour stays
 * data-driven per course.
 */

const HEX6 = /^#[0-9a-fA-F]{6}$/;

/** Parse a hex (#rgb / #rrggbb) or rgb()/rgba() string to [r, g, b]. */
export function parseColor(color, fallback = [29, 170, 97]) {
  const c = String(color || "").trim();
  if (c.startsWith("#")) {
    let hex = c.slice(1);
    if (hex.length === 3) hex = hex.split("").map((h) => h + h).join("");
    if (hex.length !== 6) return fallback;
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  if (c.startsWith("rgb")) {
    const m = c.match(/rgba?\(([^)]+)\)/);
    if (m) {
      const parts = m[1].split(",").map((p) => parseInt(p.trim(), 10));
      return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
    }
  }
  return fallback;
}

/** Lighten toward white by `percent` (matches Blade lightenColor, default 75). */
export function lightenColor(color, percent = 75) {
  const [r, g, b] = parseColor(color);
  const f = (v) => Math.min(255, Math.round(v + ((255 - v) * percent) / 100));
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
}

/** Darken toward black by `percent` (matches Blade darkenColor, default 15). */
export function darkenColor(color, percent = 15) {
  const [r, g, b] = parseColor(color);
  const f = (v) => Math.max(0, Math.round(v * (1 - percent / 100)));
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
}

const safe = (v, fb) => (HEX6.test(String(v || "")) ? v : fb);

/**
 * Build the CSS-variable object for a course's theme. Spread onto the wrapper
 * element's `style`. Templates read `--cl-primary`, `--cl-primary-hover`, etc.
 */
export function themeVars({ primary, btnText, footerBg } = {}) {
  const p = safe(primary, "#1daa61");
  const [r, g, b] = parseColor(p);
  return {
    "--cl-primary": p,
    "--cl-primary-rgb": `${r}, ${g}, ${b}`,
    "--cl-primary-hover": darkenColor(p, 15),
    "--cl-primary-light": lightenColor(p, 75),
    "--cl-primary-soft": darkenColor(p, 40),
    "--cl-primary-glow": `rgba(${r}, ${g}, ${b}, 0.15)`,
    "--cl-btn-text": safe(btnText, "#ffffff"),
    "--cl-footer-bg": safe(footerBg, "#0d1117"),
  };
}
